import React, { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Button, CircularProgress, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { useDispatch, useSelector } from "react-redux";
import { refreshUserUser, selectUser } from "../../../Store/userSlice";
import ReservationService from "../../../api/ReservationService";
import { toast } from "react-toastify";
import { FaCoins, FaMoneyBillWave } from "react-icons/fa";
import "./BookingPage.css";

const MONTHS_FR = [
  "jan.","fév.","mars","avr.","mai","juin",
  "juil.","août","sep.","oct.","nov.","déc.",
];
const DAYS_FR = ["Dim.","Lun.","Mar.","Mer.","Jeu.","Ven.","Sam."];

function fmtSlot(slot) {
  const [h, m] = slot.split(":");
  return `${h.padStart(2, "0")}:${m.padStart(2, "0")}`;
}
function addNinety(slot) {
  const [h, m] = slot.split(":").map(Number);
  const t = h * 60 + m + 90;
  return `${String(Math.floor(t/60)%24).padStart(2,"0")}:${String(t%60).padStart(2,"0")}`;
}
function fmtDate(d) {
  const date = new Date(d + "T12:00:00");
  return `${DAYS_FR[date.getDay()]} ${date.getDate()} ${MONTHS_FR[date.getMonth()]} ${date.getFullYear()}`;
}
function extractSlotKey(iso) {
  const p = iso.split("T")[1];
  return `${Number(p.split(":")[0])}:${p.split(":")[1]}`;
}

export default function BookingPage() {
  const navigate    = useNavigate();
  const dispatch    = useDispatch();
  const user        = useSelector(selectUser);
  const [params]    = useSearchParams();

  const dateStr  = params.get("date") || "";
  const slotKey  = params.get("slot") || "";

  const [courts,        setCourts]       = useState([]);
  const [selectedCourt, setSelectedCourt] = useState(null);
  const [payment,       setPayment]      = useState("cash");
  const [places,        setPlaces]       = useState(4);
  const [loading,       setLoading]      = useState(false);
  const [fetching,      setFetching]     = useState(true);
  const [step,          setStep]         = useState("form");   // "form" | "success"
  const [createdResId,  setCreatedResId] = useState(null);
  const [copied,        setCopied]       = useState(false);

  /* ── Load free courts for this slot ─────────────────── */
  const loadCourts = useCallback(async () => {
    if (!dateStr || !slotKey) return;
    setFetching(true);
    try {
      const terrainsRes = await ReservationService.getTerrain();
      const all = terrainsRes.data;

      const results = await Promise.all(
        all.map((t) =>
          ReservationService.getReservationsByDate(dateStr, t._id)
            .then((r) => ({ terrain: t, reservations: r.data }))
            .catch(() => ({ terrain: t, reservations: [] }))
        )
      );

      const free = results
        .filter(({ reservations }) =>
          !reservations.some((r) => extractSlotKey(r.start) === slotKey)
        )
        .map(({ terrain }) => ({
          _id: terrain._id,
          name: terrain.name,
        }));

      if (free.length === 0) {
        toast.warn("Ce créneau n'a plus de terrain disponible.", { theme: "light" });
        navigate(-1);
        return;
      }

      setCourts(free);
      setSelectedCourt(free[0]);
    } catch {
      toast.error("Erreur lors du chargement.", { theme: "light" });
    }
    setFetching(false);
  }, [dateStr, slotKey, navigate]);

  useEffect(() => { loadCourts(); }, [loadCourts]);

  if (!dateStr || !slotKey) return <Navigate replace to="/reservation-v2" />;

  /* ── Confirm booking ─────────────────────────────────── */
  const handleConfirm = async () => {
    if (!selectedCourt) return;
    setLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem("token")).accessToken;
      const slotPadded = slotKey.length === 4 ? "0" + slotKey : slotKey;
      const body = {
        payementMethod: payment,
        terrain_id: selectedCourt._id,
        start: `${dateStr}T${slotPadded}:00.000+00:00`,
        howMuchPaid: payment === "cash" ? 0 : places,
        restByCash: payment === "cash",
        byCash: payment === "cash",
        numberOfPlacesByCash: payment === "cash" ? places : 0,
      };
      const res = await ReservationService.createResrevation(token, body);
      const resId = res.data?._id ?? res.data?.reservation?._id ?? null;
      dispatch(refreshUserUser());
      setCreatedResId(resId);
      setStep("success");
    } catch (e) {
      toast.error(
        e.response?.data?.message || "La réservation n'a pas pu être créée.",
        { theme: "light" }
      );
    }
    setLoading(false);
  };

  /* ── Share link ──────────────────────────────────────── */
  const shareLink = createdResId
    ? `${window.location.origin}/reservation/join/${createdResId}`
    : null;

  const handleCopy = () => {
    if (!shareLink) return;
    navigator.clipboard.writeText(shareLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  /* ── Derived ─────────────────────────────────────────── */
  const slotDisplay = fmtSlot(slotKey);
  const endDisplay  = addNinety(slotKey);
  const dateLabel   = fmtDate(dateStr);

  const notEnoughJetons =
    payment === "jetons" &&
    user?.jetons !== undefined &&
    user.jetons < places;

  /* ── Render: loading ─────────────────────────────────── */
  if (fetching) {
    return (
      <div className="phone-page" style={{ justifyContent: "center", alignItems: "center" }}>
        <CircularProgress sx={{ color: "#1a5689" }} />
      </div>
    );
  }

  /* ── Render: success ─────────────────────────────────── */
  if (step === "success") {
    return (
      <div className="phone-page">
        <div className="phone-header">
          <div className="phone-header-titles">
            <div className="phone-header-title">Réservation confirmée</div>
          </div>
        </div>

        <div className="phone-body">
          <div className="bk-success">
            <div className="bk-success-ring">✓</div>
            <div className="bk-success-title">Terrain réservé !</div>
            <div className="bk-success-detail">
              {slotDisplay} – {endDisplay}<br />
              {dateLabel}<br />
              <strong>{selectedCourt?.name}</strong>
            </div>

            {shareLink && (
              <div className="bk-share-card" style={{ width: "100%" }}>
                <div className="bk-share-label">Invitez vos partenaires</div>
                <div className="bk-share-desc">
                  Partagez ce lien pour que vos amis puissent rejoindre votre réservation.
                </div>
                <Button
                  variant="outlined"
                  startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
                  onClick={handleCopy}
                  color={copied ? "success" : "primary"}
                  className="bk-copy-btn"
                >
                  {copied ? "Lien copié !" : "Copier le lien d'invitation"}
                </Button>
              </div>
            )}

            <Button
              variant="contained"
              onClick={() => navigate("/my-reservation")}
              className="bk-myres-btn"
            >
              Voir mes réservations
            </Button>
            <Button
              variant="text"
              onClick={() => navigate("/reservation-v2")}
              className="bk-myres-btn"
              color="inherit"
            >
              Retour aux créneaux
            </Button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Render: form ────────────────────────────────────── */
  return (
    <div className="phone-page">
      {/* Header */}
      <div className="phone-header">
        <IconButton
          className="phone-header-back"
          onClick={() => navigate(-1)}
          size="small"
        >
          <ArrowBackIcon />
        </IconButton>
        <div className="phone-header-titles">
          <div className="phone-header-title">Réserver un terrain</div>
          <div className="phone-header-sub">{slotDisplay} – {endDisplay} &nbsp;•&nbsp; {dateLabel}</div>
        </div>
      </div>

      <div className="phone-body">

        {/* Slot summary */}
        <div className="bk-slot-pill">
          <div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
              <span className="bk-slot-pill-time">{slotDisplay}</span>
              <span className="bk-slot-pill-sep">–</span>
              <span className="bk-slot-pill-end">{endDisplay}</span>
            </div>
            <div style={{ fontSize: 13, opacity: 0.8, marginTop: 4 }}>1h30 de jeu</div>
          </div>
          <div className="bk-slot-pill-date">{dateLabel}</div>
        </div>

        {/* Court selector */}
        <div className="bk-card">
          <div className="bk-card-label">Choisissez un terrain</div>
          <div className="bk-court-list">
            {courts.map((court) => (
              <div
                key={court._id}
                className="bk-court-item"
                onClick={() => setSelectedCourt(court)}
              >
                <div className={`bk-court-radio ${selectedCourt?._id === court._id ? "bk-court-radio--active" : ""}`}>
                  {selectedCourt?._id === court._id && <div className="bk-court-radio-dot" />}
                </div>
                <span className="bk-court-name">{court.name}</span>
                <span className="bk-court-badge">Libre</span>
              </div>
            ))}
          </div>
        </div>

        {/* Payment method */}
        <div className="bk-card">
          <div className="bk-card-label">Mode de paiement</div>
          <div className="bk-pay-row">
            {[
              { key: "jetons", icon: <FaCoins style={{ color: "#f59e0b" }} />, label: "Jetons" },
              { key: "cash",   icon: <FaMoneyBillWave style={{ color: "#16a34a" }} />, label: "Espèces" },
            ].map(({ key, icon, label }) => (
              <div
                key={key}
                className={`bk-pay-tab ${payment === key ? "bk-pay-tab--active" : ""}`}
                onClick={() => { setPayment(key); if (key === "jetons") setPlaces(1); }}
              >
                <div className="bk-pay-tab-icon">{icon}</div>
                <div className="bk-pay-tab-label">{label}</div>
              </div>
            ))}
          </div>

          {/* Jeton balance */}
          {payment === "jetons" && user?.jetons !== undefined && (
            <div className="bk-balance">
              <FaCoins style={{ color: "#f59e0b", flexShrink: 0 }} />
              <span>Solde actuel : <strong>{user.jetons} jeton{user.jetons > 1 ? "s" : ""}</strong></span>
            </div>
          )}
          {notEnoughJetons && (
            <div className="bk-balance-error">
              ⚠️ Solde insuffisant — vous avez {user.jetons} jeton{user.jetons > 1 ? "s" : ""} mais vous en sélectionnez {places}
            </div>
          )}
        </div>

        {/* Places count */}
        <div className="bk-card">
          <div className="bk-card-label">
            Nombre de {payment === "jetons" ? "jetons" : "places"}
          </div>
          <div className="bk-places-grid">
            {[1, 2, 3, 4].map((n) => {
              const tooMany = payment === "jetons" && user?.jetons !== undefined && n > user.jetons;
              return (
                <div
                  key={n}
                  className={`bk-place-item ${places === n ? "bk-place-item--active" : ""} ${tooMany ? "bk-place-item--disabled" : ""}`}
                  onClick={() => !tooMany && setPlaces(n)}
                >
                  {n}
                </div>
              );
            })}
          </div>
        </div>

      </div>

      {/* Sticky CTA */}
      <div className="phone-footer">
        <Button
          variant="contained"
          className="phone-footer-btn"
          disabled={!selectedCourt || loading || notEnoughJetons}
          onClick={handleConfirm}
          sx={{ bgcolor: "#1a5689", "&:hover": { bgcolor: "#15446e" } }}
        >
          {loading
            ? <CircularProgress size="1.3rem" sx={{ color: "white" }} />
            : "Confirmer la réservation"}
        </Button>
      </div>
    </div>
  );
}
